import { useState, useRef, useEffect } from 'react';
import { Delete02Icon, Link01Icon, PencilIcon, PinIcon, Unlink01Icon } from 'hugeicons-react';
import { ChevronDown } from 'lucide-react';
import '../../Styles/Header/TitleHeader.css';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { useChatContext } from '../../contexts/AppDataContext';
import React from 'react';
import { useUnifiedData } from '../../contexts/AppDataContext';
import { deleteChat } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useWindow } from '../../contexts/WindowContext';
import { useToast } from '../../contexts/ToastContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRow,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export default function TitleHeader(): React.ReactElement | null {
  const navigate = useNavigate();
  const { currentChatId: chatId, memoizedChatTitle, isSpaceChat } = useChatContext();
  const { windowWidth } = useWindow();
  const { removeChat, renameChat, chats, togglePinChat } = useUnifiedData();
  const auth = useAuth();
  const { openConfirmationModal } = useWindow();
  const toast = useToast();
  const shareChatMutation = useMutation("chats:shareChat" as any);
  const unshareChatMutation = useMutation("chats:unshareChat" as any);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentTitle, setCurrentTitle] = useState<string>(memoizedChatTitle);

  const currentChat = React.useMemo(() => {
    return chats?.find((chat: any) => chat.id === chatId) || null;
  }, [chats, chatId]);

  useEffect(() => {
    setCurrentTitle(currentChat?.title || memoizedChatTitle);
  }, [currentChat, memoizedChatTitle]);


  const handleUpdateTitle = (): void => {
    if (currentTitle.trim().length === 0) {
      return;
    }
    renameChat(chatId || '', currentTitle);
    setIsEditing(false);
  };

  const confirmDeleteChat = (): void => {
    deleteChat(chatId || '');
    removeChat(chatId || '');
    localStorage.removeItem(`chat_${chatId}`);
    navigate(`/new`, {
      replace: true,
    });
  };

  const handleDeleteChat = (): void => {
    openConfirmationModal(
      `Are you sure you want to delete "${memoizedChatTitle || 'this chat'}"?`,
      confirmDeleteChat,
      'This action cannot be undone.'
    );
  };


  const shareArgs = () => ({
    id: chatId || '',
    userId: String(auth.user?._id ?? ''),
    legacyUserId: auth.user?.legacyId ?? null,
  });

  const copyShareLink = async (shareId: string): Promise<void> => {
    const url = `${window.location.origin}/share/${shareId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast('Link Copied', 'success');
    } catch {
      toast(url, 'default');
    }
  };

  const handleShareLink = async (): Promise<void> => {
    if (!chatId || !auth.user?._id) return;
    try {
      const { shareId } = await shareChatMutation(shareArgs());
      await copyShareLink(shareId);
    } catch (error) {
      console.error('Error sharing chat:', error);
      toast('Failed to Share Chat', 'error');
    }
  };

  const confirmStopSharing = async (): Promise<void> => {
    try {
      await unshareChatMutation(shareArgs());
      toast('Link Disabled', 'success');
    } catch (error) {
      console.error('Error unsharing chat:', error);
      toast('Failed to Stop Sharing', 'error');
    }
  };

  const handleStopSharing = (): void => {
    openConfirmationModal(
      'Stop sharing this chat?',
      confirmStopSharing,
      'Anyone with the link will lose access. Sharing again creates a new link.'
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleUpdateTitle();
      setIsEditing(false);
    }
  };

  if (
    (!memoizedChatTitle ||
      memoizedChatTitle === '' ||
      memoizedChatTitle === 'New Chat') &&
    currentChat === null
  ) {
    return null;
  }

  if (
    !currentChat ||
    (!auth.subscription && windowWidth < 600) ||
    (isSpaceChat && windowWidth < 500)
  ) {
    return null;
  }

  if (isEditing) {
    return (
      <div className="title-header-container">
        <div
          ref={containerRef}
          className={`chat-title-container editing`}
        >
          <input
            type="text"
            value={currentTitle}
            className="chat-title-input"
            onChange={(e) => setCurrentTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              handleUpdateTitle();
              setIsEditing(false);
            }}
            autoFocus
          />
          <ChevronDown className="title-menu-icon editing" size={14} strokeWidth={2.5} aria-hidden />
        </div>
      </div>
    );
  }

  return (
    <div className="title-header-container">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div
            ref={containerRef}
            className={`chat-title-container not-editing`}
          >
            <h1 className="chat-title">{memoizedChatTitle || 'New Chat'}</h1>
            <ChevronDown className="title-menu-icon not-editing" size={14} strokeWidth={2.5} aria-hidden />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuRow
            icon={(
              <PinIcon
                color={currentChat?.isPinned ? "#FFB800" : undefined}
                fill={currentChat?.isPinned ? "#FFB800" : "none"}
              />
            )}
            text={currentChat?.isPinned ? 'Unpin' : 'Pin'}
            action={() => {
              if (chatId) togglePinChat(chatId);
            }}
          />
          <DropdownMenuRow
            icon={<Link01Icon />}
            text={currentChat?.isShared ? 'Copy Link' : 'Share Link'}
            action={() => {
              if (currentChat?.isShared && currentChat.shareId) {
                copyShareLink(currentChat.shareId);
              } else {
                handleShareLink();
              }
            }}
          />
          {currentChat?.isShared && (
            <DropdownMenuRow
              icon={<Unlink01Icon />}
              text="Stop Sharing"
              action={handleStopSharing}
            />
          )}
          <DropdownMenuRow
            icon={<PencilIcon />}
            text="Rename"
            action={() => {
              setIsEditing(true);
            }}
          />
          <DropdownMenuRow
            action={handleDeleteChat}
            icon={<Delete02Icon />}
            text="Delete Chat"
            className="text-[var(--destructive)] hover:text-[var(--destructive)] hover:bg-[var(--destructive)]/10"
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
